// A stream belongs to one selection generation, not merely a model ID: switching
// away and back must not revive a response from the abandoned connection.
import { splitThinking } from './thinking.js';

export function createModelConversations(initialModel, { now = () => performance.now() } = {}) {
  const conversations = new Map();
  let model = initialModel;
  let generation = 0;
  const read = () => conversations.get(model) || [];
  const write = (messages) => { conversations.set(model, messages); return messages; };
  const timingFor = (message, text, done, status = 'completed') => {
    const timing = message.timing;
    if (!timing || timing.durationMs !== null) return timing;
    const elapsed = Math.max(0, now() - timing.startedAt);
    const answer = splitThinking(text, { expected: !!message.thinkingExpected,
      streaming: !done || status !== 'completed' }).answer.trim();
    return { ...timing, firstReplyMs: timing.firstReplyMs ?? (answer ? elapsed : null),
      ...(done ? { durationMs: elapsed, status } : {}) };
  };
  const invalidate = () => {
    generation += 1;
    write(read().map((message) => message.streaming ? { ...message, streaming: false,
      ...(message.timing ? { timing: timingFor(message, message.text, true, 'interrupted') } : {}) } : message));
  };
  return {
    read,
    remove(targetModel) {
      if (targetModel === model) invalidate();
      conversations.delete(targetModel);
    },
    select(nextModel) {
      invalidate();
      model = nextModel;
      return read();
    },
    replace(messages) {
      invalidate();
      return write(messages);
    },
    beginReply(message) {
      invalidate();
      const user = read().at(-1);
      const userId = user?.role === "user" ? user.id : null;
      write([...read().map((item) => item.id === userId ? { ...item, policyEligible: false } : item),
        { ...message, policyEligible: false,
          timing: { startedAt: now(), firstReplyMs: null, durationMs: null, status: 'streaming' } }]);
      return { model, generation, id: message.id, userId };
    },
    isCurrent(token) {
      return token.model === model && token.generation === generation;
    },
    updateReply(token, text, done, result = { status: "completed" }) {
      if (!this.isCurrent(token)) return null;
      const policyEligible = done && result.status === "completed";
      return write(read().map((message) => {
        if (message.id === token.id) return { ...message, text, streaming: !done,
          timing: timingFor(message, text, done, result.status),
          policyEligible, ...(done ? { error: result.error || null, stopped: result.status === 'cancelled' } : {}) };
        if (message.id === token.userId) return { ...message, policyEligible };
        return message;
      }));
    },
  };
}

export function modelSessionKey(model, metadata = {}) {
  return JSON.stringify([model, metadata.releaseId || "", metadata.harnessProfile || ""]);
}
